import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import LoadingSpinner from '../components/LoadingSpinner';
import { getPaymentAdminDetail, updatePaymentStatus } from '../api/paymentsApi';
import { useToast } from '../context/ToastContext';

function PaymentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPayment = async () => {
    try {
      const result = await getPaymentAdminDetail(id);
      if (result.success) {
        setPayment(result.payment);
      } else {
        showToast('error', result.message || 'Failed to load payment.');
      }
    } catch (err) {
      showToast('error', 'Could not connect to server.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleToggleStatus = async () => {
    const newStatus = payment.pay_status === 'paid' ? 'not paid yet' : 'paid';
    const result = await updatePaymentStatus(id, newStatus);
    if (result.success) {
      showToast('success', `Payment marked as ${newStatus}.`);
      fetchPayment();
    } else {
      showToast('error', result.message || 'Failed to update payment.');
    }
  };

  if (loading || !payment) {
    return (
      <div style={{ display: 'flex' }}>
        <Sidebar />
        <div style={{ flex: 1 }}>
          <TopBar title="Loading..." />
          <LoadingSpinner fullScreen />
        </div>
      </div>
    );
  }

  const isPaid = payment.pay_status === 'paid';

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <div style={{ flex: 1 }}>
        <TopBar title={`Payment #${payment.id_payment}`} />

        <div style={{ padding: 24, maxWidth: 480 }}>
          <div style={styles.card}>
            <div style={styles.topRow}>
              <span style={styles.driverName}>{payment.driver_name}</span>
              <span
                style={{
                  ...styles.statusPill,
                  backgroundColor: isPaid ? '#d1fae5' : '#fef3c7',
                  color: isPaid ? '#065f46' : '#92400e',
                }}
              >
                {isPaid ? 'Paid' : 'Not Paid Yet'}
              </span>
            </div>

            {/* Both the real load price and the driver's actual payout,
                shown side by side so admins can sanity-check the math. */}
            <div style={styles.amountRow}>
              <div>
                <div style={styles.amountLabel}>Load Price</div>
                <div style={styles.loadPriceValue}>
                  {Number(payment.load_price).toLocaleString()} {payment.currency}
                </div>
              </div>
              <div>
                <div style={styles.amountLabel}>Driver Pay</div>
                <div style={styles.amount}>
                  {Number(payment.amount_value).toLocaleString()} {payment.currency}
                </div>
              </div>
            </div>

            <div style={styles.divider} />

            <div style={styles.row}>
              <span style={styles.rowLabel}>Load</span>
              <span
                style={{ ...styles.rowValue, color: '#2563eb', cursor: 'pointer' }}
                onClick={() => navigate(`/loads/${payment.load_id}`)}
              >
                #{payment.load_id} — {payment.origin} → {payment.destination}
              </span>
            </div>
            <div style={styles.row}>
              <span style={styles.rowLabel}>Rate</span>
              <span style={styles.rowValue}>{payment.rate_percentage}%</span>
            </div>
            <div style={styles.row}>
              <span style={styles.rowLabel}>Created</span>
              <span style={styles.rowValue}>{payment.created_at?.split(' ')[0]}</span>
            </div>
            <div style={styles.row}>
              <span style={styles.rowLabel}>Payment Date</span>
              <span style={styles.rowValue}>{payment.payment_date || '—'}</span>
            </div>

            <button
              onClick={handleToggleStatus}
              style={{
                ...styles.toggleBtn,
                backgroundColor: isPaid ? '#fee2e2' : '#d1fae5',
                color: isPaid ? '#991b1b' : '#065f46',
              }}
            >
              {isPaid ? 'Revert to Not Paid' : 'Mark as Paid'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  card: { backgroundColor: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  topRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },
  driverName: { fontSize: 16, fontWeight: 700, color: '#111827' },
  statusPill: { padding: '4px 12px', borderRadius: 999, fontSize: 11, fontWeight: 700 },
  amountRow: { display: 'flex', gap: 24, marginBottom: 16 },
  amountLabel: { fontSize: 11, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 },
  loadPriceValue: { fontSize: 20, fontWeight: 700, color: '#374151' },
  amount: { fontSize: 20, fontWeight: 700, color: '#2563eb' },
  divider: { height: 1, backgroundColor: '#f3f4f6', marginBottom: 12 },
  row: { display: 'flex', justifyContent: 'space-between', marginBottom: 10 },
  rowLabel: { fontSize: 13, color: '#6b7280' },
  rowValue: { fontSize: 13, fontWeight: 600, color: '#111827' },
  toggleBtn: { width: '100%', border: 'none', borderRadius: 8, padding: '11px 0', fontSize: 14, fontWeight: 700, cursor: 'pointer', marginTop: 16 },
};

export default PaymentDetail;